"use client"

import { createContext, useContext, useEffect } from "react"
import { toast } from "sonner"
// Local storage key
const URL_PARAMS_KEY = "blendai_url_params"

// Context
const UrlParamsContext = createContext<null>(null)

export function UrlParamsProvider({ children }: { children: React.ReactNode }) {
    useEffect(() => {
        // Only run on client side
        if (typeof window === "undefined") return

        // Only capture params on the homepage for now
        if (window.location.pathname !== "/") return

        // Get URL search params
        const searchParams = new URLSearchParams(window.location.search)
        
        // If no search params, don't proceed
        if (searchParams.toString() === "") return
        
        // Create data object with current URL info
        const newParamsData = {
            full_url: window.location.href,
            path: window.location.pathname,
            timestamp: Date.now(),
        }
        
        // Add all URL parameters to the data object
        for (const [key, value] of searchParams.entries()) {
            // If this is a redirect back after successful authentication, don't store params
            if (key === 'auth' && value === 'success') {
                localStorage.removeItem(URL_PARAMS_KEY)
                return
            }
            // @ts-expect-error - Dynamic property assignment
            newParamsData[key] = value
        }
        
        // Retrieve existing params from localStorage
        let existingData = []
        try {
            const storedData = localStorage.getItem(URL_PARAMS_KEY)
            if (storedData) {
                existingData = JSON.parse(storedData)
                if (!Array.isArray(existingData)) existingData = []
            }
        } catch (error) {
            console.error("Error parsing stored URL params:", error)
            existingData = []
        }
        
        // Add new data to the array and store back
        existingData.push(newParamsData)
        
        // Limit to last 100 entries to prevent localStorage size issues
        if (existingData.length > 100) {
            existingData = existingData.slice(-100)
        }
        
        // Save back to localStorage
        localStorage.setItem(URL_PARAMS_KEY, JSON.stringify(existingData))
    }, [])
    
    // This provider doesn't expose any methods via context
    // It just silently tracks URL parameters in localStorage
    return (
        <UrlParamsContext.Provider value={null}>
            {children}
        </UrlParamsContext.Provider>
    )
}

// Helper to get the stored URL parameters
export function getStoredUrlParams(deleteAfterRetrieval?: boolean) {
    if (typeof window === "undefined") return []
    
    try {
        const storedData = localStorage.getItem(URL_PARAMS_KEY)
        if (storedData) {
            const parsedData = JSON.parse(storedData)
            return Array.isArray(parsedData) ? parsedData : []
        }
    } catch (error) {
        console.error("Error retrieving stored URL params:", error)
    }

    if (deleteAfterRetrieval) {
        localStorage.removeItem(URL_PARAMS_KEY)
    }
    
    return []
} 